import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  Users,
  GraduationCap,
  Calendar as CalendarIcon,
  ClipboardCheck,
  BarChart3,
  Clock,
  MapPin,
} from "lucide-react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Recharts palette — matches the Analytics page so both surfaces feel unified.
const CHART_COLORS = [
  "#4A90D9",
  "#E5634D",
  "#50C878",
  "#FFB347",
  "#9B59B6",
  "#1ABC9C",
  "#E67E22",
  "#3498DB",
];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#11141A] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-white font-body font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-body" style={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
        </p>
      ))}
    </div>
  );
}

function truncateLabel(v) {
  return typeof v === "string" && v.length > 22 ? v.slice(0, 22) + "…" : v;
}

function formatTimeShort(t) {
  if (!t) return "";
  try {
    const [h, m] = String(t).split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch {
    return t;
  }
}

function formatDateShort(d) {
  if (!d) return "";
  try {
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const firstName = user?.full_name?.split(" ")[0] || "Admin";
  const learnerLabel = settings.learner_label || "Learner";

  const [stats, setStats] = useState({
    active_cohorts: 0,
    total_learners: 0,
    upcoming_events: 0,
    upcoming_events_preview: [],
    historical_assessment_types: [],
  });
  const [recent, setRecent] = useState({ cohorts: [], assessment_types: [] });
  const [selectedTypeId, setSelectedTypeId] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true,
      });
      setStats({
        active_cohorts: data.active_cohorts ?? 0,
        total_learners: data.total_learners ?? 0,
        upcoming_events: data.upcoming_events ?? 0,
        upcoming_events_preview: data.upcoming_events_preview || [],
        historical_assessment_types: data.historical_assessment_types || [],
      });
    } catch {
      /* fallback to zeros */
    }
  }, []);

  const fetchRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/dashboard/recent-cohort-scores`,
        { withCredentials: true },
      );
      setRecent({
        cohorts: data.cohorts || [],
        assessment_types: data.assessment_types || [],
      });
    } catch {
      setRecent({ cohorts: [], assessment_types: [] });
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchRecent();
  }, [fetchStats, fetchRecent]);

  // Default the dropdown to the first available historical assessment type.
  useEffect(() => {
    if (!selectedTypeId && stats.historical_assessment_types.length > 0) {
      setSelectedTypeId(stats.historical_assessment_types[0].assessment_type_id);
    }
  }, [stats.historical_assessment_types, selectedTypeId]);

  const selectedType = useMemo(
    () =>
      stats.historical_assessment_types.find(
        (t) => t.assessment_type_id === selectedTypeId,
      ) || null,
    [stats.historical_assessment_types, selectedTypeId],
  );

  // Bar-chart data — flatten the { cohort_name, scores: {type: mean} } shape.
  const chartData = useMemo(
    () =>
      recent.cohorts.map((c) => ({ name: c.cohort_name, ...(c.scores || {}) })),
    [recent.cohorts],
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h2
          className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight"
          data-testid="dashboard-welcome"
        >
          Welcome, {firstName}
        </h2>
        <p className="text-[#6E7787] font-body text-sm mt-1">
          Here&apos;s an overview of your {settings.app_name || "DiNAXLP"} platform.
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch"
        data-testid="dashboard-stat-row"
      >
        {/* Active Cohorts */}
        <div
          className="bg-[#11141A] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all duration-200 h-full flex flex-col"
          data-testid="stat-card-cohorts"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[#6E7787] font-body text-sm">Active Cohorts</p>
              <p className="text-3xl font-heading font-bold text-white mt-2">
                {stats.active_cohorts}
              </p>
            </div>
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor:
                  "var(--content-icon-bg, var(--primary-color, #4A90D9))",
                color: "var(--content-icon-color, #FFFFFF)",
              }}
            >
              <Users className="h-5 w-5" style={{ color: "inherit" }} />
            </div>
          </div>
        </div>

        {/* Total Learners */}
        <div
          className="bg-[#11141A] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all duration-200 h-full flex flex-col"
          data-testid="stat-card-learners"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[#6E7787] font-body text-sm">
                Total {learnerLabel}s
              </p>
              <p className="text-3xl font-heading font-bold text-white mt-2">
                {stats.total_learners}
              </p>
            </div>
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor:
                  "var(--content-icon-bg, var(--primary-color, #4A90D9))",
                color: "var(--content-icon-color, #FFFFFF)",
              }}
            >
              <GraduationCap className="h-5 w-5" style={{ color: "inherit" }} />
            </div>
          </div>
        </div>

        {/* Upcoming Events — inline detail preview of the next 3 events. */}
        <button
          type="button"
          onClick={() => navigate("/admin/events")}
          className="text-left bg-[#11141A] border border-white/10 rounded-xl p-6 hover:border-white/30 transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color,#4A90D9)] h-full flex flex-col"
          data-testid="stat-card-upcoming-events"
          aria-label="View upcoming events"
        >
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1 pr-2">
              <p className="text-[#6E7787] font-body text-sm">Upcoming Events</p>
              <p
                className="text-3xl font-heading font-bold text-white mt-2"
                data-testid="upcoming-events-count"
              >
                {stats.upcoming_events}
              </p>
            </div>
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
              style={{
                backgroundColor:
                  "var(--content-icon-bg, var(--primary-color, #4A90D9))",
                color: "var(--content-icon-color, #FFFFFF)",
              }}
            >
              <CalendarIcon className="h-5 w-5" style={{ color: "inherit" }} />
            </div>
          </div>

          <div className="mt-3 flex-1 min-h-0" data-testid="upcoming-events-preview">
            {stats.upcoming_events_preview.length === 0 ? (
              <p
                className="text-[11px] text-[#6E7787] font-body italic"
                data-testid="upcoming-events-empty"
              >
                Nothing scheduled yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {stats.upcoming_events_preview.slice(0, 3).map((ev) => (
                  <li
                    key={ev.id}
                    className="min-w-0"
                    data-testid={`upcoming-events-item-${ev.id}`}
                  >
                    <p
                      className="text-xs font-body font-medium text-white truncate"
                      title={ev.name}
                    >
                      {ev.name}
                    </p>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] font-body text-[#6E7787]">
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <CalendarIcon className="h-2.5 w-2.5" />
                        {formatDateShort(ev.date)}
                      </span>
                      {ev.start_time ? (
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <Clock className="h-2.5 w-2.5" />
                          {formatTimeShort(ev.start_time)}
                        </span>
                      ) : null}
                      {ev.location ? (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <MapPin className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{ev.location}</span>
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
                {stats.upcoming_events > stats.upcoming_events_preview.length ? (
                  <li className="text-[10px] font-body text-[#6E7787] pt-0.5">
                    +
                    {stats.upcoming_events -
                      stats.upcoming_events_preview.length}{" "}
                    more
                  </li>
                ) : null}
              </ul>
            )}
          </div>
        </button>

        {/* Pre-Assessment Completion — with dropdown filter. */}
        <div
          className="bg-[#11141A] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all duration-200 h-full flex flex-col"
          data-testid="stat-card-pre-completion"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[#6E7787] font-body text-sm">
                Pre-Assessment Completion
              </p>
              <p
                className="text-3xl font-heading font-bold text-white mt-2"
                data-testid="pre-completion-pct"
              >
                {selectedType ? `${selectedType.completion_pct}%` : "—"}
              </p>
              {selectedType && (
                <p className="text-[11px] text-[#6E7787] font-body mt-1">
                  {selectedType.completed}/{selectedType.total_learners}{" "}
                  {learnerLabel.toLowerCase()}s ·{" "}
                  {selectedType.cohorts_count} cohort
                  {selectedType.cohorts_count === 1 ? "" : "s"}
                </p>
              )}
            </div>
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
              style={{
                backgroundColor:
                  "var(--content-icon-bg, var(--primary-color, #4A90D9))",
                color: "var(--content-icon-color, #FFFFFF)",
              }}
            >
              <ClipboardCheck className="h-5 w-5" style={{ color: "inherit" }} />
            </div>
          </div>
          <div className="mt-3">
            {stats.historical_assessment_types.length === 0 ? (
              <p
                className="text-[11px] text-[#6E7787] font-body italic"
                data-testid="pre-completion-empty"
              >
                No historical assessments assigned to any active cohort.
              </p>
            ) : (
              <select
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                data-testid="pre-completion-type-select"
                className="w-full h-8 rounded-md bg-[#0A0C10] border border-white/10 text-white text-xs font-body px-2 focus:outline-none focus:border-[var(--primary-color,#4A90D9)]"
              >
                {stats.historical_assessment_types.map((t) => (
                  <option key={t.assessment_type_id} value={t.assessment_type_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Recent-cohort assessment averages — bar chart matches Analytics style. */}
      <div
        className="bg-[#11141A] border border-white/10 rounded-xl p-5 overflow-hidden"
        data-testid="dashboard-recent-scores"
      >
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-heading text-base font-semibold text-white">
              Recent Cohort Assessment Averages
            </h3>
            <p className="text-[11px] text-[#6E7787] font-body mt-0.5">
              Last three cohorts with assessment data
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/analytics")}
            className="text-xs font-body text-[#A1A9B8] hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/5"
            data-testid="recent-scores-view-analytics"
          >
            View analytics →
          </button>
        </div>
        {recent.cohorts.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="recent-scores-empty"
          >
            <BarChart3
              className="h-8 w-8 mb-2"
              style={{ color: "var(--primary-color, #4A90D9)" }}
            />
            <p className="text-sm text-[#A1A9B8] font-body">
              No cohorts with assessment data yet.
            </p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 0, bottom: 60 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#6E7787", fontSize: 11 }}
                  tickFormatter={truncateLabel}
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={70}
                  tickMargin={6}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: "#6E7787", fontSize: 11 }}
                  width={36}
                />
                <Tooltip content={<ChartTooltip />} />
                {recent.assessment_types.map((t, i) => (
                  <Bar
                    key={t.id}
                    dataKey={t.name}
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-white/5 mt-2">
              {recent.assessment_types.map((t, i) => (
                <div key={t.id} className="flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-sm shrink-0"
                    style={{
                      backgroundColor:
                        CHART_COLORS[i % CHART_COLORS.length],
                    }}
                  />
                  <span className="text-xs text-[#A1A9B8] font-body">
                    {t.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
